/* Recovery AWEARness — Sunrise redesign behavior. No dependencies. */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const DAY = 86400000;
  const STORE_KEY = 'rd-clean-date';
  const cleanups = new WeakMap();

  const store = {
    get(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { window.localStorage.setItem(key, value); } catch (e) { /* storage unavailable */ } },
    remove(key) { try { window.localStorage.removeItem(key); } catch (e) { /* storage unavailable */ } },
  };

  const addCleanup = (root, fn) => {
    const list = cleanups.get(root) || [];
    list.push(fn);
    cleanups.set(root, list);
  };

  const onVisible = (el, cb) => {
    if (!('IntersectionObserver' in window)) { cb(true); return () => {}; }
    const io = new IntersectionObserver((entries) => entries.forEach((e) => cb(e.isIntersecting)), { rootMargin: '80px' });
    io.observe(el);
    return () => io.disconnect();
  };

  /* ----------------------------------------------------------------------
     Hero: the featured shirt card tilts toward a mouse pointer (desktop only)
     ---------------------------------------------------------------------- */
  function initHero(root) {
    if (root.dataset.rdReady) return;
    root.dataset.rdReady = 'true';
    const caseEl = root.querySelector('[data-rd-case]');
    if (!caseEl) return;

    const onMove = (event) => {
      if (event.pointerType === 'touch' || !finePointer.matches || reduceMotion.matches) return;
      const hr = root.getBoundingClientRect();
      caseEl.style.setProperty('--tx', (((event.clientX - hr.left) / hr.width) * 2 - 1).toFixed(3));
      caseEl.style.setProperty('--ty', (((event.clientY - hr.top) / hr.height) * 2 - 1).toFixed(3));
    };
    const onLeave = () => {
      caseEl.style.setProperty('--tx', '0');
      caseEl.style.setProperty('--ty', '0');
    };

    root.addEventListener('pointermove', onMove);
    root.addEventListener('pointerleave', onLeave);
    addCleanup(root, () => {
      root.removeEventListener('pointermove', onMove);
      root.removeEventListener('pointerleave', onLeave);
    });
  }

  /* ----------------------------------------------------------------------
     Top 10: color swatches and the mobile rail meter
     ---------------------------------------------------------------------- */
  function initTop10(root) {
    root.querySelectorAll('[data-rd-card]').forEach((card) => {
      const links = card.querySelectorAll('[data-rd-card-link]');
      card.querySelectorAll('[data-rd-swatch]').forEach((button) => {
        const color = button.dataset.rdSwatch;
        button.addEventListener('click', () => {
          card.dataset.color = color;
          card.removeAttribute('data-preview');
          card.querySelectorAll('[data-rd-swatch]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
          const href = color === 'white' ? card.dataset.hrefWhite : card.dataset.hrefBlack;
          if (href) links.forEach((a) => { a.href = href; });
        });
        button.addEventListener('pointerenter', (e) => { if (e.pointerType !== 'touch') card.dataset.preview = color; });
        button.addEventListener('pointerleave', () => card.removeAttribute('data-preview'));
      });
    });

    const rail = root.querySelector('[data-rd-rail]');
    const bar = root.querySelector('[data-rd-rail-bar]');
    if (rail && bar) {
      const update = () => {
        const max = rail.scrollWidth - rail.clientWidth;
        const progress = max > 0 ? rail.scrollLeft / max : 1;
        bar.style.transform = `scaleX(${(0.12 + 0.88 * progress).toFixed(3)})`;
      };
      rail.addEventListener('scroll', update, { passive: true });
      update();
    }
  }

  /* ----------------------------------------------------------------------
     Milestones: carousel + clean-date counter
     ---------------------------------------------------------------------- */
  const addTime = (date, amount, unit) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (unit === 'months') {
      const day = d.getDate();
      d.setDate(1);
      d.setMonth(d.getMonth() + amount);
      const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      d.setDate(Math.min(day, last));
    } else {
      d.setDate(d.getDate() + amount);
    }
    return d;
  };
  const daysBetween = (a, b) => Math.round((b - a) / DAY);
  const today = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()); };
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const spanText = (from, to) => {
    let years = to.getFullYear() - from.getFullYear();
    let months = to.getMonth() - from.getMonth();
    let days = to.getDate() - from.getDate();
    if (days < 0) { months -= 1; days += new Date(to.getFullYear(), to.getMonth(), 0).getDate(); }
    if (months < 0) { years -= 1; months += 12; }
    const parts = [];
    if (years) parts.push(plural(years, 'year'));
    if (months) parts.push(plural(months, 'month'));
    if (days || !parts.length) parts.push(plural(days, 'day'));
    return parts.join(', ');
  };
  const parseDate = (value) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  function initVault(root) {
    const track = root.querySelector('[data-rd-track]');
    const stones = [...root.querySelectorAll('[data-rd-stone]')];
    const dots = [...root.querySelectorAll('[data-rd-dot]')];
    if (!track || !stones.length) return;

    let data = [];
    try { data = JSON.parse(root.querySelector('[data-rd-stones]')?.textContent || '[]'); } catch (e) { data = []; }

    let current = -1;
    let autoplay = 0;
    let userTook = false;
    let inView = false;
    let scrollRaf = 0;

    const setCurrent = (index) => {
      if (index === current) return;
      current = index;
      stones.forEach((s, i) => s.classList.toggle('is-active', i === index));
      dots.forEach((d, i) => d.setAttribute('aria-current', i === index ? 'true' : 'false'));
    };

    const go = (index, smooth = true) => {
      const i = (index + stones.length) % stones.length;
      const stone = stones[i];
      const left = stone.offsetLeft - (track.clientWidth - stone.offsetWidth) / 2;
      track.scrollTo({ left, behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto' });
      setCurrent(i);
    };

    const detect = () => {
      scrollRaf = 0;
      const mid = track.scrollLeft + track.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      stones.forEach((s, i) => {
        const d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - mid);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      setCurrent(best);
    };

    const stopAuto = () => { if (autoplay) window.clearInterval(autoplay); autoplay = 0; };
    const startAuto = () => {
      if (autoplay || userTook || !inView || reduceMotion.matches) return;
      autoplay = window.setInterval(() => go(current + 1), 4200);
    };
    const takeOver = () => { userTook = true; stopAuto(); };

    track.addEventListener('scroll', () => { if (!scrollRaf) scrollRaf = requestAnimationFrame(detect); }, { passive: true });
    track.addEventListener('pointerdown', takeOver);
    track.addEventListener('wheel', takeOver, { passive: true });
    track.addEventListener('focusin', takeOver);
    track.addEventListener('mouseenter', stopAuto);
    track.addEventListener('mouseleave', startAuto);
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); takeOver(); go(current + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); takeOver(); go(current - 1); }
    });
    root.querySelector('[data-rd-prev]')?.addEventListener('click', () => { takeOver(); go(current - 1); });
    root.querySelector('[data-rd-next]')?.addEventListener('click', () => { takeOver(); go(current + 1); });
    dots.forEach((dot, i) => dot.addEventListener('click', () => { takeOver(); go(i); }));

    const stopObserving = onVisible(track, (v) => { inView = v; if (v) startAuto(); else stopAuto(); });
    detect();

    /* Counter */
    const count = root.querySelector('[data-rd-count]');
    if (count) initCounter(count, { stones, dots, data, go, takeOver });

    addCleanup(root, () => { stopAuto(); stopObserving(); });
  }

  function initCounter(count, vault) {
    const form = count.querySelector('[data-rd-count-form]');
    const input = count.querySelector('[data-rd-date]');
    const error = count.querySelector('[data-rd-error]');
    const empty = count.querySelector('[data-rd-empty]');
    const result = count.querySelector('[data-rd-result]');
    const out = {
      gem: count.querySelector('[data-rd-gem]'),
      days: count.querySelector('[data-rd-days]'),
      unit: count.querySelector('[data-rd-unit]'),
      span: count.querySelector('[data-rd-span]'),
      earned: count.querySelector('[data-rd-earned]'),
      meter: count.querySelector('[data-rd-meter]'),
      next: count.querySelector('[data-rd-next]'),
      wear: count.querySelector('[data-rd-wear]'),
    };
    if (!form || !input || !result) return;

    input.max = isoDate(today());
    let countRaf = 0;
    let shareText = '';

    const showError = (message) => {
      error.textContent = message;
      error.hidden = !message;
      input.setAttribute('aria-invalid', message ? 'true' : 'false');
    };

    const animateNumber = (to) => {
      if (countRaf) cancelAnimationFrame(countRaf);
      if (reduceMotion.matches || to < 2) { out.days.textContent = to.toLocaleString(); return; }
      const t0 = performance.now();
      const duration = Math.min(1600, 500 + to * 2);
      const step = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        out.days.textContent = Math.round(to * eased).toLocaleString();
        if (p < 1) countRaf = requestAnimationFrame(step);
      };
      countRaf = requestAnimationFrame(step);
    };

    const resetStones = () => {
      vault.stones.forEach((s) => {
        s.classList.remove('is-earned', 'is-locked', 'is-here');
        const status = s.querySelector('[data-rd-status]');
        if (status) status.textContent = '';
      });
      vault.dots.forEach((d) => d.classList.remove('is-locked'));
    };

    const render = (clean, { scroll = true } = {}) => {
      const now = today();
      const days = daysBetween(clean, now);
      const targets = vault.data.map((s) => addTime(clean, Number(s.amount) || 0, s.unit));
      let here = -1;
      targets.forEach((t, i) => { if (t <= now) here = i; });
      if (here < 0) here = 0;

      resetStones();
      vault.stones.forEach((s, i) => {
        const status = s.querySelector('[data-rd-status]');
        if (i <= here) {
          s.classList.add('is-earned');
          if (status) status.textContent = i === here ? "You're here" : 'Earned';
        } else {
          s.classList.add('is-locked');
          vault.dots[i]?.classList.add('is-locked');
          if (status) status.textContent = `In ${plural(daysBetween(now, targets[i]), 'day')}`;
        }
      });
      vault.stones[here]?.classList.add('is-here');

      const stone = vault.data[here] || {};
      const nextIndex = here + 1 < targets.length ? here + 1 : -1;

      const gemSource = vault.stones[here]?.querySelector('.rd-gem');
      out.gem.innerHTML = '';
      if (gemSource) out.gem.appendChild(gemSource.cloneNode(true));

      out.unit.textContent = days === 1 ? 'day' : 'days';
      animateNumber(days);
      out.span.textContent = days === 0 ? 'Day one. It counts.' : `${spanText(clean, now)} clean`;
      out.earned.innerHTML = '';
      out.earned.append("You've reached ");
      const b = document.createElement('b');
      b.textContent = stone.name || stone.label || 'your next milestone';
      out.earned.append(b, '.');

      if (nextIndex > -1) {
        const prev = targets[here];
        const next = targets[nextIndex];
        const total = Math.max(1, daysBetween(prev, next));
        const done = Math.min(total, Math.max(0, daysBetween(prev, now)));
        const left = daysBetween(now, next);
        out.meter.style.setProperty('--p', (done / total).toFixed(3));
        const nextStone = vault.data[nextIndex];
        out.next.textContent = `${nextStone.name} (${nextStone.label}) in ${plural(left, 'day')}.`;
      } else {
        out.meter.style.setProperty('--p', '1');
        out.next.textContent = 'Every milestone reached. Keep stacking years.';
      }

      if (stone.url && stone.product) {
        out.wear.innerHTML = '';
        out.wear.append('Wear it: ');
        const a = document.createElement('a');
        a.href = stone.url;
        a.textContent = stone.product;
        out.wear.append(a);
        out.wear.hidden = false;
      } else {
        out.wear.hidden = true;
      }

      shareText = `${days.toLocaleString()} ${days === 1 ? 'day' : 'days'} clean. ${stone.name || 'A new milestone'} reached.`;
      if (empty) empty.hidden = true;
      result.hidden = false;
      if (scroll) { vault.takeOver(); vault.go(here); }
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const clean = parseDate(input.value);
      if (!clean) { showError('Pick your clean date to count your days.'); return; }
      if (clean > today()) { showError('Pick today or a date before it.'); return; }
      showError('');
      store.set(STORE_KEY, isoDate(clean));
      render(clean);
    });

    count.querySelector('[data-rd-reset]')?.addEventListener('click', () => {
      store.remove(STORE_KEY);
      input.value = '';
      resetStones();
      result.hidden = true;
      if (empty) empty.hidden = false;
      showError('');
      input.focus();
    });

    count.querySelector('[data-rd-share]')?.addEventListener('click', async (event) => {
      const button = event.currentTarget;
      const text = `${shareText} ${window.location.origin}`;
      try {
        if (navigator.share) { await navigator.share({ text }); return; }
        await navigator.clipboard.writeText(text);
        const label = button.textContent;
        button.textContent = 'Copied';
        window.setTimeout(() => { button.textContent = label; }, 1800);
      } catch (e) { /* share dismissed */ }
    });

    const saved = parseDate(store.get(STORE_KEY));
    if (saved && saved <= today()) {
      input.value = isoDate(saved);
      render(saved, { scroll: false });
    }
  }

  /* ----------------------------------------------------------------------
     Pull a truth
     ---------------------------------------------------------------------- */
  function initPull(root) {
    let pool = [];
    try { pool = JSON.parse(root.querySelector('[data-rd-pull-data]')?.textContent || '[]'); } catch (e) { pool = []; }
    const button = root.querySelector('[data-rd-pull-btn]');
    const quote = root.querySelector('[data-rd-pull-quote]');
    if (!button || !quote || pool.length < 2) { if (button) button.hidden = pool.length < 2; return; }

    const cut = root.querySelector('[data-rd-pull-cut]');
    const price = root.querySelector('[data-rd-pull-price]');
    const link = root.querySelector('[data-rd-pull-link]');
    const frame = root.querySelector('[data-rd-pull-imglink]');
    const img = frame?.querySelector('img');
    const glyphs = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    let index = 0;
    let order = [];
    let scrambleRaf = 0;
    let busy = false;

    const nextIndex = () => {
      if (!order.length) {
        order = pool.map((_, i) => i).filter((i) => i !== index);
        for (let i = order.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [order[i], order[j]] = [order[j], order[i]];
        }
      }
      return order.pop();
    };

    const scrambleTo = (text) => new Promise((resolve) => {
      if (reduceMotion.matches) { quote.textContent = text; resolve(); return; }
      const t0 = performance.now();
      const duration = 650;
      const step = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        const reveal = Math.floor(text.length * p);
        let s = text.slice(0, reveal);
        for (let i = reveal; i < text.length; i += 1) {
          s += text[i] === ' ' ? ' ' : glyphs[Math.floor(Math.random() * glyphs.length)];
        }
        quote.textContent = s;
        if (p < 1) scrambleRaf = requestAnimationFrame(step); else resolve();
      };
      if (scrambleRaf) cancelAnimationFrame(scrambleRaf);
      scrambleRaf = requestAnimationFrame(step);
    });

    const swapImage = (src) => new Promise((resolve) => {
      if (!img || !src) { resolve(); return; }
      const pre = new Image();
      pre.onload = pre.onerror = () => {
        frame.classList.add('is-swapping');
        window.setTimeout(() => {
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = src;
          frame.classList.remove('is-swapping');
          if (!reduceMotion.matches) {
            frame.classList.remove('is-flash');
            void frame.offsetWidth;
            frame.classList.add('is-flash');
          }
          resolve();
        }, reduceMotion.matches ? 0 : 220);
      };
      pre.src = src;
    });

    button.addEventListener('click', async () => {
      if (busy) return;
      busy = true;
      index = nextIndex();
      const item = pool[index];
      if (link) link.href = item.u;
      if (frame) frame.href = item.u;
      if (cut) cut.textContent = item.c;
      if (price) price.textContent = item.p;
      await Promise.all([scrambleTo(item.t), swapImage(item.i)]);
      busy = false;
    });
  }

  /* ----------------------------------------------------------------------
     Story: the sunrise draws itself as the section scrolls into view
     ---------------------------------------------------------------------- */
  function initStory(root) {
    const svg = root.querySelector('[data-rd-draw]');
    if (!svg) return;
    if (reduceMotion.matches) { svg.style.setProperty('--draw', '1'); return; }
    let raf = 0;
    const update = () => {
      raf = 0;
      const r = root.getBoundingClientRect();
      const vh = window.innerHeight || 800;
      const p = Math.min(1, Math.max(0, (vh - r.top) / (vh * 0.95)));
      svg.style.setProperty('--draw', p.toFixed(3));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    const stop = onVisible(root, (v) => {
      if (v) { window.addEventListener('scroll', onScroll, { passive: true }); update(); }
      else window.removeEventListener('scroll', onScroll);
    });
    addCleanup(root, () => { stop(); window.removeEventListener('scroll', onScroll); });
  }

  /* ----------------------------------------------------------------------
     Boot + theme editor support
     ---------------------------------------------------------------------- */
  const init = (scope = document) => {
    scope.querySelectorAll('[data-rd-hero]').forEach(initHero);
    scope.querySelectorAll('[data-rd-top10]').forEach(initTop10);
    scope.querySelectorAll('[data-rd-vault]').forEach(initVault);
    scope.querySelectorAll('[data-rd-pull]').forEach(initPull);
    scope.querySelectorAll('[data-rd-story]').forEach(initStory);
  };
  const teardown = (scope) => {
    scope.querySelectorAll('[data-rd-hero],[data-rd-vault],[data-rd-story]').forEach((root) => {
      (cleanups.get(root) || []).forEach((fn) => fn());
      cleanups.delete(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => teardown(e.target));
})();
